function normalizeSkillLevelId(raw?: string | null): string | null {
  const value = raw?.trim();
  if (!value) return null;
  if (/^sk-\d+$/i.test(value)) return value.toLowerCase();
  const match = value.match(/^sk(\d+)$/i);
  if (match) return `sk-${match[1]}`;
  return value.toLowerCase();
}

/** Highest catalog tier (sk-6). Nothing is "higher" than this. */
const HIGHEST_SKILL_RANK = 6;
const AND_HIGHER_SUFFIX = " and higher";

/** Numeric rank for sorting skill levels low → high (sk-1 = 1 … sk-6 = 6). */
export function skillLevelRank(skillId?: string | null): number {
  const id = normalizeSkillLevelId(skillId);
  if (id && id.startsWith("sk-")) {
    const n = Number(id.slice(3));
    if (Number.isFinite(n)) return n;
  }
  return Number.MAX_SAFE_INTEGER;
}

/** Compare two skill-level ids low → high. */
export function compareSkillLevelIds(
  a?: string | null,
  b?: string | null,
): number {
  return skillLevelRank(a) - skillLevelRank(b);
}

/** Sort option-like objects by their skill-level value, low → high. */
export function sortSkillOptionsByLevel<T extends { value?: string | null }>(
  options: T[],
): T[] {
  return [...options].sort((a, b) => compareSkillLevelIds(a.value, b.value));
}

/** Spoken AU aliases per catalog id — mirrors backend ShiftAssistantSkillLevelGuide. */
export const SKILL_LEVEL_SPOKEN_ALIASES: Record<string, string[]> = {
  "sk-1": [
    "PGY2+",
    "PGY 2",
    "PGY2",
    "PGY1",
    "PGY 1",
    "intern",
    "internship",
    "HMO",
    "resident",
    "junior doctor",
    "house officer",
  ],
  "sk-2": [
    "PGY3+",
    "PGY 3",
    "PGY3",
    "RMO",
    "senior resident",
    "resident medical officer",
  ],
  "sk-3": ["PGY4+", "PGY 4", "PGY4", "PGY5", "PGY6", "SRMO", "senior RMO"],
  "sk-4": ["registrar", "PHO", "advanced trainee", "fellow"],
  "sk-5": ["fellow", "senior registrar", "provisional fellow"],
  "sk-6": [
    "consultant",
    "specialist",
    "staff specialist",
    "VMO",
    "SMO",
    "CMO",
    "career medical officer",
    "FACEM",
    "GP",
  ],
};

/** Universal labels — job title and PGY tier together (e.g. HMO / PGY2+). */
export function combinedSkillLevelLabel(
  skillId?: string | null,
  canonicalName?: string | null,
): string {
  const id = normalizeSkillLevelId(skillId);
  if (id) {
    switch (id) {
      case "sk-1":
        return "HMO / PGY2+";
      case "sk-2":
        return "RMO / PGY3+";
      case "sk-3":
        return "SRMO / PGY4+";
      case "sk-4":
        return "Registrar";
      case "sk-5":
        return "SMO / VMO";
      case "sk-6":
        return "Consultant / GP";
      default:
        break;
    }
  }
  const canonical = canonicalName?.trim();
  if (canonical) return canonical;
  return skillId?.trim() ?? "";
}

/** Dropdown / tag label including spoken aliases doctors actually use. */
export function skillLevelLabelWithAliases(
  skillId?: string | null,
  canonicalName?: string | null,
): string {
  const primary = combinedSkillLevelLabel(skillId, canonicalName);
  const id = normalizeSkillLevelId(skillId);
  const aliases = id ? SKILL_LEVEL_SPOKEN_ALIASES[id] || [] : [];
  if (!aliases.length) return primary;

  const primaryLower = primary.toLowerCase();
  const extras = aliases.filter(
    (alias) => !primaryLower.includes(alias.toLowerCase()),
  );
  if (!extras.length) return primary;
  return `${primary} · ${extras.slice(0, 4).join(" · ")}`;
}

/**
 * Label for a shift's MINIMUM skill requirement, e.g. "HMO / PGY2+ and higher".
 * The top tier (sk-6) gets no suffix — nothing is higher than Consultant / GP.
 * Mirrors backend SkillLevelDisplayUtil.resolveMinimumLabel.
 */
export function skillLevelMinimumLabel(
  skillId?: string | null,
  canonicalName?: string | null,
): string {
  const base = combinedSkillLevelLabel(skillId, canonicalName);
  if (!base) return base;
  // If the value already carries the suffix (e.g. came from the backend), keep it once.
  if (base.toLowerCase().includes("and higher")) return base;
  if (skillLevelRank(skillId) >= HIGHEST_SKILL_RANK) return base;
  return `${base}${AND_HIGHER_SUFFIX}`;
}

/** @deprecated Use combinedSkillLevelLabel */
export function simplifiedSkillLevelLabel(skillId?: string | null): string {
  return combinedSkillLevelLabel(skillId);
}

export function displaySkillLevelLabel(args: {
  skillId?: string | null;
  canonicalName?: string | null;
}): string {
  return skillLevelLabelWithAliases(args.skillId, args.canonicalName);
}

export function looksLikeSkillLevelId(value?: string | null): boolean {
  const normalized = normalizeSkillLevelId(value);
  return !!normalized && /^sk-\d+$/.test(normalized);
}
